/* eslint-disable no-console */
import VertxEventBus from '@vertx/eventbus-bridge-client.js';

// project-specific config
import { TENANT } from 'constants/misc';
import { Handler, Registration } from './EventBusUpdated.types';

const STEP = 5_000;
const MAX = 60_000;

let eb: VertxEventBus | null = null;
let queue: Registration[] = []; // pending until open
let regs: Registration[] = []; // active registrations
let reconnectDelay = 0;
let giveUp = false;

// helpers
const fullUrl = () => {
  const base = window.location.origin;

  const u = new URL(base);
  u.pathname = u.pathname.replace(/\/$/, '') + '/eventbus';
  return u.toString();
};

const openBus = () => {
  if (eb) return;

  eb = new VertxEventBus(fullUrl(), { vertxbus_ping_interval: 2_000 });

  eb.onopen = () => {
    console.log('[EB] connected', new Date());
    reconnectDelay = 0;

    // Flush queued regs
    queue.splice(0).forEach((r) => {
      eb!.registerHandler(r.address, r.handler);
      regs.push(r);
    });
  };

  eb.onclose = (e) => {
    console.warn('[EB] closed', e);
    eb = null;

    if (giveUp) return;

    setTimeout(openBus, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay + STEP, MAX);

    if (reconnectDelay >= MAX) {
      console.error('[EB] giving up after repeated failures');
      giveUp = true;
    }
  };
};

// Public API — compatible with the old Dojo wrapper
function register(address: string, handler: Handler) {
  address = TENANT + address;

  // If we previously gave up, let explicit usage revive the bus
  if (giveUp) {
    giveUp = false;
    reconnectDelay = 0; // start fresh
  }

  // no auto-start at module load
  if (!eb) {
    openBus();
  }

  // If already open, register immediately; otherwise queue it
  if (eb && eb.state === VertxEventBus.OPEN) {
    eb.registerHandler(address, handler);
    regs.push({ address, handler });
    console.log('[EB] handler registered for', address);
  } else {
    queue.push({ address, handler });
  }
}

function unregister(address: string, handler: Handler) {
  address = TENANT + address;
  if (eb.state === VertxEventBus.OPEN) {
    eb.unregisterHandler(address, handler);
  }
  regs = regs.filter((r) => r.address !== address || r.handler !== handler);
  console.log('[EB] handler un-registered for', address);
}

export default {
  register,
  unregister,
  getRegistered: () => regs.slice(),
  isRegistered: (addr: string, h: Handler) =>
    regs.some((r) => r.address.includes(addr) && r.handler === h),
  close: () => eb?.close()
};

export const FLEX_TASKS_ADDR = '/secure/users/flexportal/tasks';
export const OPEN_REQUESTS_ADDR = '/roster/open-requests';
